import { Download } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useEditor, track } from 'tldraw';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/Tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/molecules/DropdownMenu';

export const ExportButton = track(() => {
  const editor = useEditor();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const chronologyId = searchParams.get('chronologyId');

  const handleExport = async (format: 'png' | 'svg') => {
    if (!editor) return;

    const shapeIds = editor.getCurrentPageShapeIds();
    if (shapeIds.size === 0) {
      toast.error('No shapes on the canvas');
      return;
    }

    trackCustomEvent(TrackingEvents.ExportVisualChronology, {
      caseId: caseId,
      chronologyId: chronologyId,
    });

    try {
      const { blob } = await editor.toImage([...shapeIds], {
        format,
        background: true,
      });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `chronology-timeline-${new Date().toISOString().split('T')[0]}.${format}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error exporting timeline:', error);
      alert('Failed to export timeline');
    }
  };

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <div
              tabIndex={0}
              className="flex cursor-pointer rounded-md border border-gray-100 p-2 shadow-sm transition-colors hover:bg-gray-100"
              role="button"
            >
              <Download className="h-4 w-4" />
            </div>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Export</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="bg-white">
        <DropdownMenuItem className="cursor-pointer hover:bg-gray-100" onClick={() => handleExport('png')}>
          Export as PNG
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer hover:bg-gray-100" onClick={() => handleExport('svg')}>
          Export as SVG
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
